import * as THREE from "three";

/**
 * Uniform-grid broadphase over the level's colliders.
 *
 * The clearing had ~100 boxes and a linear scan was free.  A street with three
 * houses, six gardens and a wood is closer to a thousand, and the player tests
 * them three times per axis-pass, twice per pass, every frame -- that is the
 * kind of arithmetic that quietly turns into a frame budget.  Bucketing by XZ
 * means each query touches only the handful of boxes actually nearby.
 *
 * Boxes are stored by reference, so the narrow phase in player.js is unchanged.
 */
export class ColliderGrid {
  constructor(colliders, cell = 4) {
    this.cell = cell;
    this.all = colliders;
    this.cells = new Map();
    this.stamp = new Int32Array(colliders.length);
    this.stamp.fill(-1);
    this.tick = 0;
    this.out = [];

    colliders.forEach((c, i) => {
      const [x0, z0] = this.coord(c.min.x, c.min.z);
      const [x1, z1] = this.coord(c.max.x, c.max.z);
      for (let x = x0; x <= x1; x++) {
        for (let z = z0; z <= z1; z++) {
          const k = key(x, z);
          let bucket = this.cells.get(k);
          if (!bucket) this.cells.set(k, (bucket = []));
          bucket.push(i);
        }
      }
    });
  }

  coord(x, z) {
    return [Math.floor(x / this.cell), Math.floor(z / this.cell)];
  }

  /**
   * Colliders whose cells overlap `box`.
   *
   * The returned array is reused between calls -- copy it if you need to hold
   * on to the result past the next query.
   */
  near(box) {
    const out = this.out;
    out.length = 0;
    const t = ++this.tick;
    const [x0, z0] = this.coord(box.min.x, box.min.z);
    const [x1, z1] = this.coord(box.max.x, box.max.z);
    for (let x = x0; x <= x1; x++) {
      for (let z = z0; z <= z1; z++) {
        const bucket = this.cells.get(key(x, z));
        if (!bucket) continue;
        for (const i of bucket) {
          // A box spanning several cells appears in each of them; the stamp
          // keeps it out of the result more than once.
          if (this.stamp[i] === t) continue;
          this.stamp[i] = t;
          out.push(this.all[i]);
        }
      }
    }
    return out;
  }

  /** Colliders registered in the single cell containing (x, z). */
  nearPoint(x, z) {
    const bucket = this.cells.get(key(...this.coord(x, z)));
    if (!bucket) return EMPTY;
    const out = [];
    for (const i of bucket) out.push(this.all[i]);
    return out;
  }

  /**
   * Distance to the nearest hit along `ray`, capped at maxDist.
   *
   * Used to pull the camera in when the world gets between it and the cat.
   * Only boxes in the cells the ray's bounding box touches are tested, which
   * on a street of a thousand colliders is a couple of dozen.
   */
  raycast(ray, maxDist) {
    const end = _v.copy(ray.direction).multiplyScalar(maxDist).add(ray.origin);
    _seg.makeEmpty();
    _seg.expandByPoint(ray.origin);
    _seg.expandByPoint(end);
    let best = maxDist;
    for (const c of this.near(_seg)) {
      if (ray.intersectBox(c, _hit)) {
        const d = ray.origin.distanceTo(_hit);
        if (d < best) best = d;
      }
    }
    return best;
  }

  get size() {
    return this.all.length;
  }
}

const EMPTY = [];
const _v = new THREE.Vector3();
const _hit = new THREE.Vector3();
const _seg = new THREE.Box3();

function key(x, z) {
  return (x + 32768) * 65536 + (z + 32768);
}
